/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IMoeV2FileDownloadData } from './moe-v2-file-download.data';
import { IMoeV2FileDownloadProps } from './moe-v2-file-download.props.autogenerated';

export interface IMoeV2FileDownloadViewProps extends IMoeV2FileDownloadProps<IMoeV2FileDownloadData> {}

export type FileTypeEnum =
    | 'DOC'
    | 'IMAGE'
    | 'EXCEL'
    | 'PPT'
    | 'PDF'
    | 'GOOGLEDOC'
    | 'AVI'
    | 'BMP'
    | 'CSV'
    | 'DOCX'
    | 'GIF'
    | 'HTM'
    | 'HTML'
    | 'JPEG'
    | 'JPG'
    | 'JSON'
    | 'MHP'
    | 'MHTML'
    | 'MSG'
    | 'PNG'
    | 'PPTX'
    | 'RTF'
    | 'SGML'
    | 'TIF'
    | 'TXT'
    | 'VSD'
    | 'WMF'
    | 'XHTML'
    | 'XLS'
    | 'XLSX'
    | 'XML'
    | 'ZIP'
    | 'GSLIDES'
    | 'EPUB'
    | 'WAV'
    | 'MP3'
    | 'WMA'
    | 'MP4'
    | 'MPEG'
    | 'MOV'
    | 'PIBX'
    | 'USDZ'
    | 'DAE'
    | 'XSN';

export interface FileDownloadConfig {
    title: string;
    files: FileDownloadItem[];
}

export interface FileDownloadItem {
    id: string;
    fileName: string;
    fileUrl: string;
    fileSize: string;
    fileType: FileTypeEnum;
    description?: string;
}

const getFileCategory = (fileType: string) => {
    const type = fileType.toUpperCase();

    // Document types
    if (['DOC', 'DOCX', 'RTF', 'GOOGLEDOC'].includes(type)) {
        return 'document';
    }

    // Spreadsheet types
    if (['EXCEL', 'XLS', 'XLSX', 'CSV'].includes(type)) {
        return 'spreadsheet';
    }

    // Presentation types
    if (['PPT', 'PPTX', 'GSLIDES'].includes(type)) {
        return 'presentation';
    }

    // PDF
    if (type === 'PDF') {
        return 'pdf';
    }

    // Image types
    if (['IMAGE', 'BMP', 'GIF', 'JPEG', 'JPG', 'PNG', 'TIF', 'WMF'].includes(type)) {
        return 'image';
    }

    // Video types
    if (['AVI', 'MP4', 'MPEG', 'MOV'].includes(type)) {
        return 'video';
    }

    // Audio types
    if (['WAV', 'MP3', 'WMA'].includes(type)) {
        return 'audio';
    }

    // Web types
    if (['HTM', 'HTML', 'XHTML', 'SGML'].includes(type)) {
        return 'web';
    }

    // Data/Code types
    if (['JSON', 'XML'].includes(type)) {
        return 'data';
    }

    // Archive types
    if (type === 'ZIP') {
        return 'archive';
    }

    // 3D/CAD types
    if (['VSD', 'USDZ', 'DAE'].includes(type)) {
        return 'cad';
    }

    // E-book
    if (type === 'EPUB') {
        return 'ebook';
    }

    // Other specialized formats
    if (['MHP', 'MHTML', 'MSG', 'PIBX', 'XSN'].includes(type)) {
        return 'specialized';
    }

    // Default fallback
    return 'fallback';
};

// Color scheme for different file categories
const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
        document: '#4A90E2', // Blue
        spreadsheet: '#10B981', // Green
        presentation: '#F59E0B', // Orange
        pdf: '#FF6B6B', // Red
        image: '#8B5CF6', // Purple
        video: '#EF4444', // Red
        audio: '#06B6D4', // Cyan
        web: '#F97316', // Orange
        data: '#6B7280', // Gray
        archive: '#8B5CF6', // Purple
        cad: '#059669', // Emerald
        ebook: '#7C3AED', // Violet
        specialized: '#6B7280', // Gray
        fallback: '#9CA3AF', // Light Gray
        default: '#6B7280' // Gray
    };
    return colors[category] || colors.default;
};

// SVG Icons for different file types
export const FileIcons = {
    // Base file icon template
    createFileIcon: (fileType: string, category: string) => {
        const color = getCategoryColor(category);
        const displayType = fileType.length > 4 ? fileType.substring(0, 4) : fileType;

        return (
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path
                    d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'
                    fill={color}
                    stroke={color}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <polyline points='14,2 14,8 20,8' fill='none' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                <text x='12' y='16' textAnchor='middle' fontSize={displayType.length > 3 ? '4' : '5'} fill='white' fontWeight='bold'>
                    {displayType}
                </text>
            </svg>
        );
    },

    // Specialized icons for specific categories
    image: (
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <rect x='3' y='3' width='18' height='18' rx='2' ry='2' fill='#8B5CF6' stroke='#8B5CF6' strokeWidth='2' />
            <circle cx='8.5' cy='8.5' r='1.5' fill='white' />
            <polyline points='21,15 16,10 5,21' stroke='white' strokeWidth='2' fill='none' />
        </svg>
    ),

    video: (
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
                d='M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z'
                fill='#EF4444'
            />
            <polygon points='9.75,15.02 15.5,11.75 9.75,8.48' fill='white' />
        </svg>
    ),

    audio: (
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path d='M9 18V5l12-2v13' fill='none' stroke='#06B6D4' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            <circle cx='6' cy='18' r='3' fill='none' stroke='#06B6D4' strokeWidth='2' />
            <circle cx='18' cy='16' r='3' fill='none' stroke='#06B6D4' strokeWidth='2' />
        </svg>
    ),

    archive: (
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <polyline
                points='21,8 21,21 3,21 3,8'
                fill='none'
                stroke='#8B5CF6'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
            <rect
                x='1'
                y='3'
                width='22'
                height='5'
                fill='#8B5CF6'
                stroke='#8B5CF6'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
            <line x1='10' y1='12' x2='14' y2='12' stroke='white' strokeWidth='2' strokeLinecap='round' />
        </svg>
    ),

    // Fallback icon for unknown file types
    fallback: (
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path
                d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'
                fill='#9CA3AF'
                stroke='#9CA3AF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />
            <polyline points='14,2 14,8 20,8' fill='none' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            <circle cx='12' cy='11' r='1' fill='white' />
            <path d='M12 13v3' stroke='white' strokeWidth='1.5' strokeLinecap='round' />
        </svg>
    )
};

// Get file icon based on file type
export const getFileIcon = (fileType: string): JSX.Element => {
    const category = getFileCategory(fileType);
    const type = fileType.toUpperCase();

    // Use specialized icons for certain categories
    switch (category) {
        case 'image':
            return FileIcons.image;
        case 'video':
            return FileIcons.video;
        case 'audio':
            return FileIcons.audio;
        case 'archive':
            return FileIcons.archive;
        case 'fallback':
            return FileIcons.fallback;
        default:
            return FileIcons.createFileIcon(type, category);
    }
};

// Info Icon SVG
export const InfoIcon = (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
        <circle cx='8' cy='8' r='7' stroke='currentColor' strokeWidth='1' />
        <path d='M8 7v4M8 5h.01' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    </svg>
);

// Download Icon SVG
export const DownloadIcon = (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
        <path d='M8 1v10M4 7l4 4 4-4M2 12h12' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

// Format file size to display as two numerals (e.g., "22kb")
export const formatFileSize = (size: string): string => {
    // Extract numbers and unit from size string
    const match = size.match(/(\d+)(\w+)/);
    if (match) {
        const [, number, unit] = match;
        return `${number}${unit.toLowerCase()}`;
    }
    return size;
};

// Utility function to detect file type from file name/extension
export const detectFileType = (fileName: string): FileTypeEnum => {
    const extension = fileName
        .split('.')
        .pop()
        ?.toUpperCase();

    const extensionMap: Record<string, FileTypeEnum> = {
        // Documents
        DOC: 'DOC',
        DOCX: 'DOCX',
        RTF: 'RTF',
        TXT: 'TXT',

        // Spreadsheets
        XLS: 'XLS',
        XLSX: 'XLSX',
        CSV: 'CSV',

        // Presentations
        PPT: 'PPT',
        PPTX: 'PPTX',

        // PDF
        PDF: 'PDF',

        // Images
        BMP: 'BMP',
        GIF: 'GIF',
        JPEG: 'JPEG',
        JPG: 'JPG',
        PNG: 'PNG',
        TIF: 'TIF',
        TIFF: 'TIF',
        WMF: 'WMF',

        // Videos
        AVI: 'AVI',
        MP4: 'MP4',
        MPEG: 'MPEG',
        MOV: 'MOV',

        // Audio
        WAV: 'WAV',
        MP3: 'MP3',
        WMA: 'WMA',

        // Web
        HTM: 'HTM',
        HTML: 'HTML',
        XHTML: 'XHTML',
        SGML: 'SGML',

        // Data
        JSON: 'JSON',
        XML: 'XML',

        // Archive
        ZIP: 'ZIP',

        // 3D/CAD
        VSD: 'VSD',
        USDZ: 'USDZ',
        DAE: 'DAE',

        // E-book
        EPUB: 'EPUB',

        // Specialized
        MHP: 'MHP',
        MHTML: 'MHTML',
        MSG: 'MSG',
        PIBX: 'PIBX',
        XSN: 'XSN'
    };

    return extensionMap[extension || ''] || 'TXT'; // Default to TXT for unknown types
};

// Utility function to format file size from bytes
export const formatBytesToSize = (bytes: number): string => {
    if (bytes === 0) {
        return '0B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i)) + sizes[i].toLowerCase();
};

// Utility function to detect file type from extension (for D365 Commerce)
export const detectFileTypeFromExtension = (extensionOrFileName: string): FileTypeEnum => {
    // Extract extension if full filename is provided
    let extension = extensionOrFileName;
    if (extensionOrFileName.includes('.')) {
        extension = extensionOrFileName.split('.').pop() || '';
    }

    // Remove leading dot if present
    extension = extension.replace(/^\./, '').toUpperCase();

    const extensionMap: Record<string, FileTypeEnum> = {
        // Documents
        DOC: 'DOC',
        DOCX: 'DOCX',
        RTF: 'RTF',
        TXT: 'TXT',

        // Spreadsheets
        XLS: 'XLS',
        XLSX: 'XLSX',
        CSV: 'CSV',

        // Presentations
        PPT: 'PPT',
        PPTX: 'PPTX',

        // PDF
        PDF: 'PDF',

        // Images
        BMP: 'BMP',
        GIF: 'GIF',
        JPEG: 'JPEG',
        JPG: 'JPG',
        PNG: 'PNG',
        TIF: 'TIF',
        TIFF: 'TIF',
        WMF: 'WMF',

        // Videos
        AVI: 'AVI',
        MP4: 'MP4',
        MPEG: 'MPEG',
        MOV: 'MOV',

        // Audio
        WAV: 'WAV',
        MP3: 'MP3',
        WMA: 'WMA',

        // Web
        HTM: 'HTM',
        HTML: 'HTML',
        XHTML: 'XHTML',
        SGML: 'SGML',

        // Data
        JSON: 'JSON',
        XML: 'XML',

        // Archive
        ZIP: 'ZIP',

        // 3D/CAD
        VSD: 'VSD',
        USDZ: 'USDZ',
        DAE: 'DAE',

        // E-book
        EPUB: 'EPUB',

        // Specialized
        MHP: 'MHP',
        MHTML: 'MHTML',
        MSG: 'MSG',
        PIBX: 'PIBX',
        XSN: 'XSN'
    };

    return extensionMap[extension] || 'TXT'; // Default to TXT for unknown types
};

// Process uploaded files from Site Builder
export const processUploadedFiles = (uploadedFiles: any[]): FileDownloadItem[] => {
    return uploadedFiles.map((fileItem, index) => {
        // Handle both old format (direct file) and new format (object with file + description)
        const file = fileItem.file || fileItem; // New format has file property, old format is direct file
        const description = fileItem.description || file.alt || undefined;

        return {
            id: `uploaded-file-${index}`,
            fileName: file.name || file.fileName || `File ${index + 1}`,
            fileUrl: file.src || file.url || file.path || '',
            fileSize: file.size ? formatBytesToSize(file.size) : '0kb',
            fileType: detectFileType(file.name || file.fileName || ''),
            description: description
        };
    });
};

/**
 *
 * MoeV2FileDownload component
 * @extends {React.PureComponent<IMoeV2FileDownloadProps<IMoeV2FileDownloadData>>}
 */
class MoeV2FileDownload extends React.PureComponent<IMoeV2FileDownloadProps<IMoeV2FileDownloadData>> {
    public render(): JSX.Element | null {
        return this.props.renderView(this.props);
    }
}

export default MoeV2FileDownload;
